import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { DefaultPageLayout } from 'record-it-components/pageLayout/DefaultPageLayout/DefaultPageLayout';
import { NavigationItem } from 'record-it-components/pageLayout/NavigationSidebar/NavigationSidebar';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { IconUtils } from '../../../classes/icon/IconUtils';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { UserProfileDialog } from '../../user/UserProfileDialog/UserProfileDialog';
export const DefaultLayout: FunctionComponent = React.memo(props => {
  const $ = _c(14);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  const {
    logout
  } = useAuth();
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  const adminWorkTimeData = useAdminWorkTimeData();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      closeOnBackDropClick: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    open: openUserProfileDialog,
    close: closeUserProfileDialog,
    modalProps: userProfileDialogProps
  } = useModalOpened(t0);
  let t1;
  if ($[1] !== adminWorkTimeData || $[2] !== getRequiredCurrentUserPermissions || $[3] !== t) {
    const currentUserPermissions = getRequiredCurrentUserPermissions();
    const firstCompilationUserGroup = currentUserPermissions.getEditableUserGroupsForTimeTrackingActivityCompilations()[0];
    t1 = getAccessibleNavigationItems([{
      ...IconUtils.thinner(iconConfigurations.prefixed.page.HomePage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.home"),
      to: RoutePaths.home(),
      isAccessible: true
    }, {
      ...IconUtils.thinner(iconConfigurations.prefixed.page.MyWorkTimeOverviewPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.myWorkTimeOverviewPage"),
      to: RoutePaths.myWorkTimeOverview(),
      isAccessible: true
    }, ...(adminWorkTimeData ? [{
      ...IconUtils.thinner(iconConfigurations.prefixed.page.MyWorkTimeOverviewPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.adminWorkTime"),
      to: RoutePaths.adminWorkTime(adminWorkTimeData.userGroupId, adminWorkTimeData.userId),
      isAccessible: true
    }] : []), {
      ...IconUtils.thinner(iconConfigurations.prefixed.page.TimeTrackingConfigurationAdminPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.timeTrackingConfiguration"),
      to: RoutePaths.adminTimeTrackingConfigurations(),
      isAccessible: currentUserPermissions.canEditTimeTrackingConfigurations()
    }, {
      ...IconUtils.thinner(iconConfigurations.prefixed.page.ConstructionSiteAdminPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.constructionSite"),
      to: RoutePaths.adminConstructionSites(),
      isAccessible: currentUserPermissions.canEditConstructionSites()
    }, ...(firstCompilationUserGroup ? [{
      ...IconUtils.thinner(iconConfigurations.prefixed.page.TimeTrackingActivityCompilationAdminPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.timeTrackingActivityCompilation"),
      to: RoutePaths.adminTimeTrackingActivityCompilations(firstCompilationUserGroup.id),
      isAccessible: true
    }] : []), {
      ...IconUtils.thinner(iconConfigurations.prefixed.page.UserAdminPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.users"),
      to: RoutePaths.adminUsers(),
      isAccessible: currentUserPermissions.canEditUsers()
    }, {
      ...IconUtils.thinner(iconConfigurations.prefixed.page.UserGroupAdminPage.titleIcon),
      label: t("components.layout.DefaultLayout.navigationItems.userGroups"),
      to: RoutePaths.adminUserGroups(),
      isAccessible: currentUserPermissions.canEditUserGroups()
    }]);
    $[1] = adminWorkTimeData;
    $[2] = getRequiredCurrentUserPermissions;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const navigationItems = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Outlet />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== closeUserProfileDialog || $[7] !== userProfileDialogProps) {
    t3 = <UserProfileDialog {...userProfileDialogProps} onAccept={closeUserProfileDialog} />;
    $[6] = closeUserProfileDialog;
    $[7] = userProfileDialogProps;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== logout || $[10] !== navigationItems || $[11] !== openUserProfileDialog || $[12] !== t3) {
    t4 = <DefaultPageLayout navigationItems={navigationItems} onLogoutClick={logout} onOpenUserProfile={openUserProfileDialog}>{t2}{t3}</DefaultPageLayout>;
    $[9] = logout;
    $[10] = navigationItems;
    $[11] = openUserProfileDialog;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
function useAdminWorkTimeData() {
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  const userGroups = getRequiredCurrentUserPermissions().getEditableUserGroupsForWorkTimeEntries();
  const userGroupToUserRepository = useEntityRepository(EntityName.UserGroupToUser);
  for (const userGroup of userGroups) {
    const firstRelation = userGroupToUserRepository.getByUserGroupId(userGroup.id)[0];
    if (firstRelation) {
      return {
        userGroupId: userGroup.id,
        userId: firstRelation.userId
      };
    }
  }
  return null;
}
function getAccessibleNavigationItems(items: Array<NavigationItem & {
  isAccessible: boolean;
}>): Array<NavigationItem> {
  return items.filter(item => item.isAccessible);
}
type AdminWorkTimeData = {
  userGroupId: string;
  userId: string;
};